import { Stack } from "@mantine/core"
import { InfoBox, LabelWithTooltip, MultiSelect } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ZodError } from "zod"

import {
  PRODUCT_ATTRIBUTES,
  ProductAttribute,
} from "@tfg/types/constants/configurations.constants"

import { i18n } from "~/i18n"

import { ValidationResult } from "../config/component-renderer"

export type ProductAttributesValidationErrors = {
  primaryProductAttributes?: ValidationResult
  secondaryProductAttributes?: ValidationResult
}

export interface ProductAttributesRendererProps {
  primaryProductAttributes?: ProductAttribute[]
  secondaryProductAttributes?: ProductAttribute[]
  setConfigurations?: (configs: {
    primaryProductAttributes?: ProductAttribute[]
    secondaryProductAttributes?: ProductAttribute[]
  }) => void
  description: string
  validationErrors: ValidationResult<{
    primaryProductAttributes?: ProductAttribute[]
    secondaryProductAttributes?: ProductAttribute[]
  }>["errors"]
}

export const ProductAttributesRenderer = ({
  primaryProductAttributes = [],
  secondaryProductAttributes = [],
  setConfigurations,
  description,
  validationErrors,
}: ProductAttributesRendererProps) => {
  const { t } = useTranslation("communicationStudio")
  const [hasPrimaryBeenTouched, setHasPrimaryBeenTouched] = useState(false)
  const productAttributeOptions = Object.values(PRODUCT_ATTRIBUTES).map(
    (attribute) => ({
      value: attribute,
      label: t(`tabs.content.productAttributes.options.${attribute}`),
    })
  )

  const handlePrimaryChange = (values: string[]) => {
    const wasTouched =
      hasPrimaryBeenTouched || primaryProductAttributes.length > 0

    if (!hasPrimaryBeenTouched && wasTouched) {
      setHasPrimaryBeenTouched(true)
    }

    if (setConfigurations) {
      setConfigurations({
        primaryProductAttributes: values as ProductAttribute[],
        secondaryProductAttributes,
      })
    }
  }

  const handleSecondaryChange = (values: string[]) => {
    if (setConfigurations) {
      setConfigurations({
        primaryProductAttributes,
        secondaryProductAttributes: values as ProductAttribute[],
      })
    }
  }

  return (
    <Stack gap="md">
      {description && (
        <InfoBox
          title={t(
            "tabs.content.componentModal.descriptions.PRODUCT_ATTRIBUTES.title"
          )}
          message={description}
        />
      )}

      <MultiSelect
        label={
          <LabelWithTooltip
            label={t("tabs.content.productAttributes.primary.label")}
            hoverHelperText={t(
              "tabs.content.productAttributes.primary.labelTooltip"
            )}
          />
        }
        labelProps={{
          w: "100%",
        }}
        size="xs"
        searchable
        placeholder={t("tabs.content.productAttributes.primary.placeholder")}
        emptyMessage={t("tabs.content.productAttributes.primary.emptyMessage")}
        value={primaryProductAttributes}
        data={productAttributeOptions}
        miw={300}
        onChange={handlePrimaryChange}
        error={
          validationErrors &&
          extractValidationError(validationErrors, "primaryProductAttributes")
        }
      />

      <MultiSelect
        label={
          <LabelWithTooltip
            label={t("tabs.content.productAttributes.secondary.label")}
            hoverHelperText={t(
              "tabs.content.productAttributes.secondary.labelTooltip"
            )}
          />
        }
        labelProps={{
          w: "100%",
        }}
        size="xs"
        searchable
        placeholder={t("tabs.content.productAttributes.secondary.placeholder")}
        emptyMessage={t(
          "tabs.content.productAttributes.secondary.emptyMessage"
        )}
        value={secondaryProductAttributes}
        data={productAttributeOptions}
        miw={300}
        error={
          validationErrors &&
          extractValidationError(validationErrors, "secondaryProductAttributes")
        }
        onChange={handleSecondaryChange}
      />
    </Stack>
  )
}

export const extractValidationError = (
  errors:
    | ValidationResult<{
        primaryProductAttributes?: ProductAttribute[]
        secondaryProductAttributes?: ProductAttribute[]
      }>["errors"]
    | undefined,
  key: "primaryProductAttributes" | "secondaryProductAttributes"
) => {
  if (!errors || !(errors instanceof ZodError)) return undefined
  let finalerrors = errors.issues
  let errorMessage: string[] = []

  for (const error of finalerrors) {
    if (error.path.join(".") === key) {
      let message = i18n.t(
        `communicationStudio:tabs.content.productAttributes.${key}.error.${error.code}`
      )
      errorMessage.push(message || error.message)
    }
  }
  return errorMessage.join(", ")
}

// need to keep these to not lose translations in parse
// t("tabs.content.productAttributes.primaryProductAttributes.error.too_small")
// t("tabs.content.productAttributes.secondaryProductAttributes.error.too_small")
